import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Fingerprint, Trash2, Plus, KeyRound } from "lucide-react";
import { listPasskeys, deletePasskey, registerPasskey } from "../api/passkey";
import { registerPasskey as browserRegisterPasskey } from "../lib/passkey";
import { queryKeys } from "../lib/queryKeys";

export function PasskeySettings() {
  const queryClient = useQueryClient();
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data: passkeysData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["passkeys", "list"],
    queryFn: () => listPasskeys(),
  });

  const passkeys = passkeysData?.passkeys ?? [];

  const handleRegister = async () => {
    setIsRegistering(true);
    setRegisterError(null);
    setRegisterSuccess(null);

    try {
      // 1. Browser creates passkey
      const credential = await browserRegisterPasskey("Paperclip Passkey");

      // 2. Send to server
      await registerPasskey({
        credentialId: credential.credentialId,
        publicKeyX: credential.publicKeyX,
        publicKeyY: credential.publicKeyY,
        name: "Paperclip Passkey",
      });

      setRegisterSuccess("Passkey registered successfully!");
      queryClient.invalidateQueries({ queryKey: ["passkeys", "list"] });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Registration failed";
      if (message.includes("cancelled") || message.includes("NotAllowedError")) {
        // User cancelled - no error
        setRegisterError(null);
      } else {
        setRegisterError(message);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deletePasskey(id);
      queryClient.invalidateQueries({ queryKey: ["passkeys", "list"] });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete passkey";
      alert(message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Passkeys</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your passkeys for passwordless authentication. Passkeys are
          stored securely on your device.
        </p>
      </div>

      {registerError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {registerError}
        </div>
      )}

      {registerSuccess && (
        <div className="rounded-md border border-green-500/50 bg-green-500/10 p-3 text-sm text-green-600">
          {registerSuccess}
        </div>
      )}

      <div className="flex items-center gap-4">
        <Button
          onClick={handleRegister}
          disabled={isRegistering}
          className="flex items-center gap-2"
        >
          {isRegistering ? (
            <>
              <KeyRound className="h-4 w-4 animate-pulse" />
              Registering...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Add Passkey
            </>
          )}
        </Button>
      </div>

      <div className="space-y-3">
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading passkeys...</div>
        )}

        {error && (
          <div className="text-sm text-destructive">
            Failed to load passkeys: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        )}

        {!isLoading && !error && passkeys.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-6 text-center">
            <Fingerprint className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              No passkeys registered yet.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Add a passkey to sign in without a password.
            </p>
          </div>
        )}

        {passkeys.map((passkey) => (
          <div
            key={passkey.id}
            className="flex items-center justify-between rounded-md border border-border p-4"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-muted p-2">
                <Fingerprint className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {passkey.name || "Passkey"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Created {formatDate(passkey.createdAt)} · Last used{" "}
                  {formatDate(passkey.lastUsedAt)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(passkey.id)}
              disabled={deletingId === passkey.id}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="rounded-md bg-muted/50 p-4 text-xs text-muted-foreground">
        <p className="font-medium text-foreground mb-1">About Passkeys</p>
        <ul className="space-y-1 list-disc pl-4">
          <li>Passkeys use your device's biometric authentication (Touch ID, Face ID, Windows Hello)</li>
          <li>Private keys never leave your device - only public keys are stored on the server</li>
          <li>You can register multiple passkeys for different devices</li>
          <li>Deleting a passkey here does not remove it from your device</li>
        </ul>
      </div>
    </div>
  );
}
